import React, { useRef, useEffect } from "react";
import {
  Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, Quote, Undo, Redo,
} from "lucide-react";

const isHtml = (str = "") => /<\/?[a-z][\s\S]*>/i.test(str);

// Convert legacy plain-text (paragraphs separated by blank lines) to HTML
export const toHtml = (content = "") =>
  isHtml(content)
    ? content
    : content
        .split("\n\n")
        .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        .join("");

export default function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = toHtml(value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || "");
  };

  const addLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const btns = [
    { icon: Bold, cmd: () => exec("bold"), label: "bold" },
    { icon: Italic, cmd: () => exec("italic"), label: "italic" },
    { icon: Underline, cmd: () => exec("underline"), label: "underline" },
    { icon: Heading2, cmd: () => exec("formatBlock", "<h2>"), label: "h2" },
    { icon: Heading3, cmd: () => exec("formatBlock", "<h3>"), label: "h3" },
    { icon: Quote, cmd: () => exec("formatBlock", "<blockquote>"), label: "quote" },
    { icon: List, cmd: () => exec("insertUnorderedList"), label: "ul" },
    { icon: ListOrdered, cmd: () => exec("insertOrderedList"), label: "ol" },
    { icon: Link2, cmd: addLink, label: "link" },
    { icon: Undo, cmd: () => exec("undo"), label: "undo" },
    { icon: Redo, cmd: () => exec("redo"), label: "redo" },
  ];

  return (
    <div className="rounded-lg border border-forest/15 bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap gap-1 border-b border-forest/10 bg-cream/60 p-1.5">
        {btns.map((b) => (
          <button
            key={b.label}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); b.cmd(); }}
            data-testid={`rte-${b.label}`}
            className="w-8 h-8 grid place-items-center rounded-md text-forest hover:bg-forest hover:text-cream transition-colors"
          >
            <b.icon size={16} />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        data-testid="rte-content"
        className="prose-editor min-h-[220px] max-h-[420px] overflow-y-auto p-4 text-sm text-ink focus:outline-none leading-relaxed"
        suppressContentEditableWarning
      />
    </div>
  );
}
